import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../lib/components/custom/Icon";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { useApi } from "../../lib/hooks/api/use-api";
import Layout from "../layout";

export default function VerifyEmail() {
	const { t } = useTranslation();
	const { auth } = useApi();
	const { data: session } = auth.useSession();
	const [isResending, setIsResending] = useState(false);
	const [resendSuccess, setResendSuccess] = useState(false);
	const [error, setError] = useState("");

	const handleResendEmail = async () => {
		setError("");
		setIsResending(true);
		setResendSuccess(false);

		try {
			const response = await auth.sendVerificationEmail({
				email: session?.user?.email ?? "",
			});

			if (response.error) {
				setError(
					response.error.message || t("auth.verify_email.errors.resend_failed"),
				);
			} else {
				setResendSuccess(true);
			}
		} catch (err) {
			setError(t("auth.verify_email.errors.resend_failed"));
			console.error("Resend verification email error:", err);
		} finally {
			setIsResending(false);
		}
	};

	return (
		<Layout>
			<div className="p-8 border min-h-full rounded-large bg-gradient-main">
				<div className="max-w-md mx-auto">
					{/* Header */}
					<div className="text-center mb-8">
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Icon name="Mail" className="w-8 h-8 text-primary" />
						</div>
						<h1 className="text-3xl font-manrope font-semibold mb-2">
							{t("auth.verify_email.header")}
						</h1>
						<p className="text-muted-foreground">
							{t("auth.verify_email.subheader")}
						</p>
					</div>

					{/* Verification Card */}
					<Card className="p-6">
						<div className="space-y-4">
							<p className="text-sm text-muted-foreground text-center">
								{t("auth.verify_email.check_inbox")}
							</p>

							{session?.user?.email && (
								<div className="text-center">
									<span className="inline-block bg-muted px-3 py-1 rounded-md text-sm font-medium">
										{session.user.email}
									</span>
								</div>
							)}

							<p className="text-sm text-muted-foreground text-center">
								{t("auth.verify_email.spam_notice")}
							</p>

							{/* Success Message */}
							{resendSuccess && (
								<div className="text-sm text-chart-2 bg-chart-2/10 border border-chart-2/20 rounded-md p-3 text-center">
									{t("auth.verify_email.resend_success")}
								</div>
							)}

							{/* Error Message */}
							{error && (
								<div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md p-3 text-center">
									{error}
								</div>
							)}

							{/* Resend Button */}
							<Button
								variant="outline"
								className="w-full"
								onClick={handleResendEmail}
								disabled={isResending || resendSuccess}
							>
								{isResending ? (
									<>
										<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
										{t("auth.verify_email.resending")}
									</>
								) : resendSuccess ? (
									<>
										<Icon name="CircleCheck" className="w-4 h-4 mr-2" />
										{t("auth.verify_email.email_sent")}
									</>
								) : (
									<>
										<Icon name="RefreshCw" className="w-4 h-4 mr-2" />
										{t("auth.verify_email.resend_button")}
									</>
								)}
							</Button>
						</div>
					</Card>

					{/* Back to Home */}
					<div className="text-center mt-4">
						<Button variant="ghost" asChild>
							<Link to="/">
								<Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
								{t("auth.signin.back_to_home")}
							</Link>
						</Button>
					</div>
				</div>
			</div>
		</Layout>
	);
}
